import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import {
  QC_ISSUE_CATEGORY_LABELS,
  QcPreviewQuestionRef,
  QcPreviewResult,
  QcPreviewScope,
} from '../../../interfaces/qcIssue.interface';
import { getQcPreviewScope, previewQcQuestion } from '../../../services/qcReview';
import { ACCENT_BLUE, SUCCESS_GREEN, ERROR_RED, FIRST_PASS_PURPLE } from '../../Assessments/Workspace/qcTheme';

// Dry-run First Pass: shows the QCIssue drafts a real run would create; the backend writes nothing, so it is safe at any status.

// A few questions at a time — each is its own Bedrock call, and a whole-assessment request would outlive LB timeouts.
const CONCURRENCY = 3;

const Shell = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  color: 'rgba(0,0,0,0.87)',
});

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 24px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

const Title = styled('h2')({
  margin: 0,
  fontSize: '1.15rem',
  fontWeight: 700,
});

const Subject = styled('span')({
  fontSize: '0.85rem',
  color: 'rgba(0,0,0,0.55)',
});

const CloseButton = styled('button')({
  marginLeft: 'auto',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '50%',
  display: 'inline-flex',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': { background: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.8)' },
});

const DryRunBanner = styled('div', {
  shouldForwardProp: prop => prop !== 'done',
})<{ done: boolean }>(({ done }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  margin: '16px 24px 0',
  padding: '11px 14px',
  borderRadius: '8px',
  border: `1px solid ${done ? 'rgba(46,125,50,0.28)' : 'rgba(94,53,177,0.22)'}`,
  background: done ? 'rgba(46,125,50,0.07)' : 'rgba(94,53,177,0.06)',
  fontSize: '0.82rem',
  color: done ? '#1b5e20' : '#4527a0',
  transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease',
  '& svg': { fontSize: 20 },
}));

const BannerHeadline = styled('strong')({
  fontWeight: 700,
});

// Determinate bar — the only honest progress signal for a job this long; shown only while a run is in flight.
const ProgressTrack = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '-1px',
  height: '3px',
  background: 'rgba(94,53,177,0.12)',
  overflow: 'hidden',
});

const ProgressFill = styled('div', {
  shouldForwardProp: prop => prop !== 'pct',
})<{ pct: number }>(({ pct }) => ({
  height: '100%',
  width: `${pct}%`,
  background: FIRST_PASS_PURPLE,
  transition: 'width 240ms ease',
}));

const ScopeControl = styled(FormControl)({
  minWidth: '186px',
});

// Wraps the control bar so the progress track sits on its bottom edge, not reading as the first result card's progress.
const ControlSection = styled('div')({
  position: 'relative',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

const ControlBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  padding: '14px 24px 12px',
});

const RunButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  border: 'none',
  borderRadius: '8px',
  padding: '9px 18px',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: '#fff',
  background: FIRST_PASS_PURPLE,
  transition: 'background 120ms ease',
  '&:hover': { background: '#4527a0' },
  '&:disabled': { background: 'rgba(0,0,0,0.16)', cursor: 'default' },
});

const StopButton = styled(RunButton)({
  color: ERROR_RED,
  background: 'rgba(198,40,40,0.1)',
  '&:hover': { background: 'rgba(198,40,40,0.18)' },
});

const RunSpinner = styled(CircularProgress)({
  color: ERROR_RED,
});

const Progress = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.6)',
});

const Tally = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: 'flag' | 'clean' | 'error' }>(({ tone }) => {
  const colors = { flag: ERROR_RED, clean: SUCCESS_GREEN, error: '#ed6c02' }[tone];
  return {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: colors,
  };
});

const ConfigLine = styled('div')({
  marginLeft: 'auto',
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.5)',
});

const Scroll = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '14px 24px 24px',
});

const StateLine = styled('p')({
  margin: '24px 0',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.6)',
});

const Row = styled('div', {
  shouldForwardProp: prop => prop !== 'flagged',
})<{ flagged: boolean }>(({ flagged }) => ({
  border: '1px solid rgba(0,0,0,0.12)',
  borderLeft: flagged ? `3px solid ${ERROR_RED}` : '1px solid rgba(0,0,0,0.12)',
  borderRadius: '4px',
  marginBottom: '8px',
  overflow: 'hidden',
}));

const RowHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  padding: '10px 14px',
  background: '#f8f9fc',
});

const Code = styled('span')({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.4px',
  color: 'rgba(0,0,0,0.6)',
  minWidth: '76px',
});

// Same destination as the QC Issues grid's link column: the workspace, on this question.
const CodeLink = styled('button')({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.4px',
  color: ACCENT_BLUE,
  minWidth: '76px',
  border: 'none',
  background: 'none',
  padding: 0,
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { textDecoration: 'underline' },
});

const QuestionTitle = styled('span')({
  fontSize: '0.82rem',
  flex: 1,
  minWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const VerdictText = styled('span', {
  shouldForwardProp: prop => prop !== 'verdict',
})<{ verdict: string }>(({ verdict }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: verdict === 'agree' ? SUCCESS_GREEN
    : verdict === 'disagree' ? ERROR_RED
      : verdict === 'uncertain' ? '#ed6c02' : '#616161',
}));

const Certainty = styled('span')({
  fontSize: '0.75rem',
  color: 'rgba(0,0,0,0.5)',
  minWidth: '46px',
  textAlign: 'right',
});

const PendingText = styled('span')({
  fontSize: '0.75rem',
  color: 'rgba(0,0,0,0.42)',
  fontStyle: 'italic',
});

const RowBody = styled('div')({
  padding: '12px 14px',
  borderTop: '1px solid rgba(0,0,0,0.07)',
});

const DraftCard = styled('div')({
  border: '1px solid rgba(198,40,40,0.25)',
  background: 'rgba(198,40,40,0.04)',
  borderRadius: '4px',
  padding: '10px 12px',
});

const DraftHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
});

const CategoryChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: '20px',
  padding: '0 9px',
  borderRadius: '10px',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: ERROR_RED,
  background: 'rgba(198,40,40,0.12)',
});

const WouldCreateLabel = styled('span')({
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.5)',
});

const DraftText = styled('p')({
  margin: 0,
  fontSize: '0.82rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.82)',
});

const NoDraft = styled('div')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.5)',
});

const Detail = styled('div')({
  marginTop: '10px',
  fontSize: '0.76rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.6)',
});

const DetailLabel = styled('span')({
  fontWeight: 600,
  color: 'rgba(0,0,0,0.5)',
});

const ErrorText = styled('span')({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#ed6c02',
});

interface QcPreviewModalProps {
  open: boolean;
  assessmentSurveyId: number;
  assessmentId?: number;
  surveyId?: number;
  subject?: string;
  onClose: () => void;
}

const formatCertainty = (value: number | null): string => (
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`
);

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  return minutes > 0 ? `${minutes}m ${totalSeconds % 60}s` : `${totalSeconds}s`;
};

// Evenly spaced rather than the first N: a leading slice only ever samples one domain.
const sampleQuestions = (questions: QcPreviewQuestionRef[], size: number): QcPreviewQuestionRef[] => {
  if (size >= questions.length) return questions;
  const step = questions.length / size;
  return Array.from({ length: size }, (_, index) => questions[Math.floor(index * step)]);
};

const SAMPLE_OPTIONS = [10, 25];

export default function QcPreviewModal({ open, assessmentSurveyId, assessmentId, surveyId, subject, onClose }: QcPreviewModalProps): JSX.Element {
  const [scope, setScope] = useState<QcPreviewScope | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [results, setResults] = useState<Record<number, QcPreviewResult>>({});
  const [failed, setFailed] = useState<Record<number, true>>({});
  const [inFlight, setInFlight] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const [sampleSize, setSampleSize] = useState<number | 'all'>(10);
  // Stamped on each completion, not ticked by an interval — frequent enough, and nothing polls to render.
  const [elapsedMs, setElapsedMs] = useState(0);
  const [finished, setFinished] = useState(false);
  const cancelled = useRef(false);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!open) return;
    setScope(null);
    setLoadError(false);
    setResults({});
    setFailed({});
    setInFlight([]);
    setRunning(false);
    setSampleSize(10);
    setElapsedMs(0);
    setFinished(false);
    cancelled.current = false;
    getQcPreviewScope(assessmentSurveyId)
      .then(setScope)
      .catch(error => {
        console.error('Failed to load QC preview scope', error);
        setLoadError(true);
      });
  }, [open, assessmentSurveyId]);

  // Unmounting mid-run must stop the queue, or workers keep firing at a closed modal.
  useEffect(() => () => { cancelled.current = true; }, []);

  const targetQuestions = scope
    ? (sampleSize === 'all' ? scope.questions : sampleQuestions(scope.questions, sampleSize))
    : [];

  const handleRun = useCallback(async (questions: QcPreviewQuestionRef[]) => {
    cancelled.current = false;
    startedAt.current = Date.now();
    setResults({});
    setFailed({});
    setElapsedMs(0);
    setFinished(false);
    setRunning(true);

    const queue = [...questions];
    const worker = async () => {
      while (queue.length && !cancelled.current) {
        const next = queue.shift();
        if (!next) return;
        setInFlight(prev => [...prev, next.assessorAnswerId]);
        try {
          const result = await previewQcQuestion(assessmentSurveyId, next.assessorAnswerId);
          if (!cancelled.current) setResults(prev => ({ ...prev, [result.assessorAnswerId]: result }));
        } catch (error) {
          console.error('QC preview failed for question', next.shortCode, error);
          if (!cancelled.current) setFailed(prev => ({ ...prev, [next.assessorAnswerId]: true }));
        } finally {
          setInFlight(prev => prev.filter(id => id !== next.assessorAnswerId));
          setElapsedMs(Date.now() - startedAt.current);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
    );
    setRunning(false);
    if (!cancelled.current) setFinished(true);
  }, [assessmentSurveyId]);

  const handleSampleChange = (event: SelectChangeEvent<string>) => {
    setSampleSize(event.target.value === 'all' ? 'all' : Number(event.target.value));
  };

  const handleStop = () => {
    cancelled.current = true;
    setInFlight([]);
    setRunning(false);
  };

  const handleClose = () => {
    cancelled.current = true;
    onClose();
  };

  const evaluated = Object.values(results);
  const flaggedCount = evaluated.filter(result => result.wouldCreateIssue).length;
  const cleanCount = evaluated.length - flaggedCount;
  const failedCount = Object.keys(failed).length;
  const total = targetQuestions.length;
  const done = evaluated.length + failedCount;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  // Projected from the run's own observed pace, so it self-corrects as it goes.
  const remainingMs = done > 0 && running ? (elapsedMs / done) * (total - done) : 0;

  return (
    <Dialog
      open={open}
      // An in-flight run is real Bedrock spend — a stray backdrop click must not discard it; only the explicit close does.
      onClose={running ? undefined : handleClose}
      disableEscapeKeyDown={running}
      // Same size vocabulary as StandardDialog's maxWidth prop; xl is the largest.
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '92vh', borderRadius: '4px', display: 'flex', flexDirection: 'column' } }}
    >
      <Shell>
        <Header>
          <Title>Preview QC Run</Title>
          {subject && <Subject>{subject}</Subject>}
          <CloseButton onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </CloseButton>
        </Header>

        <DryRunBanner done={finished}>
          <ScienceOutlinedIcon />
          {finished ? (
            <span>
              <BannerHeadline>Preview complete.</BannerHeadline>
              {' '}{flaggedCount} of {done} would create an issue
              {failedCount > 0 ? `, ${failedCount} could not be evaluated` : ''}
              , in {formatDuration(elapsedMs)}. Nothing was written.
            </span>
          ) : (
            <span>
              Dry run — nothing is written. This is exactly what a real run would create, evaluated
              through the same pipeline and the same draft rules.
            </span>
          )}
        </DryRunBanner>

        <ControlSection>
          <ControlBar>
              {running ? (
              <StopButton onClick={handleStop}>
                <RunSpinner size={14} thickness={5} />
                Stop
              </StopButton>
              ) : (
              <RunButton onClick={() => handleRun(targetQuestions)} disabled={!scope || total === 0}>
                <ScienceOutlinedIcon fontSize="small" />
                {done > 0 ? 'Run Again' : 'Run Preview'}
              </RunButton>
              )}
            {scope && scope.questions.length > 0 && (
              <ScopeControl size="small">
                <Select
                  size="small"
                  value={String(sampleSize)}
                  disabled={running}
                  onChange={handleSampleChange}
                  inputProps={{ 'aria-label': 'How many questions to preview' }}
                >
                  {SAMPLE_OPTIONS
                    .filter(size => size < scope.questions.length)
                    .map(size => (
                      <MenuItem key={size} value={String(size)}>{size} question sample</MenuItem>
                    ))}
                  <MenuItem value="all">All {scope.questions.length} questions</MenuItem>
                </Select>
              </ScopeControl>
            )}
            {total > 0 && (
              <Progress>
                {done} of {total} evaluated
                {running && elapsedMs > 0 && ` · ${formatDuration(elapsedMs)} elapsed`}
                {running && remainingMs > 0 && ` · about ${formatDuration(remainingMs)} left`}
              </Progress>
            )}
            {flaggedCount > 0 && <Tally tone="flag">{flaggedCount} would create issues</Tally>}
            {cleanCount > 0 && <Tally tone="clean">{cleanCount} clean</Tally>}
            {failedCount > 0 && <Tally tone="error">{failedCount} failed</Tally>}
            {scope && (
              <ConfigLine>
                {scope.modelId} · prompt {scope.promptVersion}
                {scope.guidanceVersion !== null ? ` · guidance v${scope.guidanceVersion}` : ' · no guidance'}
              </ConfigLine>
            )}
          </ControlBar>
          {running && (
            <ProgressTrack>
              <ProgressFill pct={pct} />
            </ProgressTrack>
          )}
        </ControlSection>

        <Scroll>
          {loadError && <StateLine>Could not load the questions for this assessment.</StateLine>}
          {!loadError && !scope && <StateLine>Loading questions…</StateLine>}
          {scope && total === 0 && <StateLine>This assessment has no questions to evaluate.</StateLine>}

          {targetQuestions.map(question => {
            const result = results[question.assessorAnswerId];
            const isFailed = failed[question.assessorAnswerId];
            const isRunning = inFlight.includes(question.assessorAnswerId);
            return (
              <Row key={question.assessorAnswerId} flagged={!!result?.wouldCreateIssue}>
                <RowHead>
                  {assessmentId && surveyId ? (
                    <CodeLink
                      type='button'
                      title='Open in assessment workspace'
                      onClick={() => window.open(
                        `${window.location.origin}/assessment/${assessmentId}/questionnaire/${surveyId}?question=${question.questionId}`,
                        '_blank',
                      )}
                    >
                      {question.shortCode || `#${question.questionId}`}
                    </CodeLink>
                  ) : (
                    <Code>{question.shortCode || `#${question.questionId}`}</Code>
                  )}
                  <QuestionTitle title={question.title}>{question.title}</QuestionTitle>
                  {result && <VerdictText verdict={result.rollup.verdict}>{result.rollup.verdict.replace('_', ' ')}</VerdictText>}
                  {result && <Certainty>{formatCertainty(result.rollup.confidence)}</Certainty>}
                  {!result && isFailed && <ErrorText>Failed</ErrorText>}
                  {!result && !isFailed && <PendingText>{isRunning ? 'Evaluating…' : 'Queued'}</PendingText>}
                </RowHead>

                {result && (
                  <RowBody>
                    {result.wouldCreate ? (
                      <DraftCard>
                        <DraftHead>
                          <WouldCreateLabel>Would create</WouldCreateLabel>
                          <CategoryChip>{QC_ISSUE_CATEGORY_LABELS[result.wouldCreate.category]}</CategoryChip>
                          <Certainty>{formatCertainty(result.wouldCreate.certainty)}</Certainty>
                        </DraftHead>
                        <DraftText>{result.wouldCreate.text}</DraftText>
                      </DraftCard>
                    ) : (
                      <NoDraft>No issue would be created.</NoDraft>
                    )}
                    {result.rollup.rationale && (
                      <Detail><DetailLabel>Rationale: </DetailLabel>{result.rollup.rationale}</Detail>
                    )}
                    {result.rollup.spellingNotes.length > 0 && (
                      <Detail><DetailLabel>Spelling: </DetailLabel>{result.rollup.spellingNotes.join('; ')}</Detail>
                    )}
                    {result.rollup.crossReferences.length > 0 && (
                      <Detail><DetailLabel>References: </DetailLabel>{result.rollup.crossReferences.join('; ')}</Detail>
                    )}
                    {result.rollup.suspectedInjection && (
                      <Detail><DetailLabel>Suspected injection — escalated for human review.</DetailLabel></Detail>
                    )}
                  </RowBody>
                )}
              </Row>
            );
          })}
        </Scroll>
      </Shell>
    </Dialog>
  );
}
